import { supabase } from '../supabase';
import type { NewsImage } from '../types/news';
import { toast } from 'react-hot-toast';

interface MediaItem {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  metadata: any;
  folder: string;
  bucket: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

const BUCKET_FOLDER_MAP = {
  'news': ['news_images', 'news'],
  'podcasts': ['Podcasts', 'podcasts', 'episodios', 'promocoes', 'thumbnails'],
  'team': ['Equipa', 'team', 'direcao', 'locutores', 'tecnicos'],
  'courses': ['Cursos', 'courses'],
  'general': ['Geral', 'general']
};

const getBucketFromFolder = (folder: string): string => {
  // Check if the folder is a UUID (article ID)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(folder)) {
    return 'news';
  }

  const normalizedFolder = folder.toLowerCase();
  for (const [bucket, folders] of Object.entries(BUCKET_FOLDER_MAP)) {
    if (folders.map(f => f.toLowerCase()).includes(normalizedFolder)) return bucket;
  }
  console.warn(`No bucket mapping found for folder: ${folder}, defaulting to 'public'`);
  return 'public';
};

const mediaService = {
  async uploadToSupabase(file: File, folder: string): Promise<NewsImage> {
    try {
      console.log('Uploading file to folder:', folder);
      const bucket = getBucketFromFolder(folder);
      
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      
      // Construct the file path based on the folder
      // If it's a UUID (article ID), place it in a subfolder
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const filePath = uuidRegex.test(folder) ? `${folder}/${fileName}` : fileName;

      // Upload file to storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (storageError) throw storageError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      // Create media file record
      const { data: mediaFile, error: mediaError } = await supabase
        .from('media_files')
        .insert({
          name: file.name,
          type: file.type,
          url: filePath,
          size: file.size,
          metadata: {
            contentType: file.type,
            originalName: file.name
          },
          folder: folder,
          bucket: bucket,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (mediaError) throw mediaError;

      console.log('Generated public URL:', publicUrl);

      // If this is an article image (folder is UUID), create a record in news_images table
      if (uuidRegex.test(folder)) {
        const { error: dbError } = await supabase
          .from('news_images')
          .insert({
            article_id: folder,
            url: filePath,
            alt: file.name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (dbError) {
          console.error('Error creating news_images record:', dbError);
          throw dbError;
        }
      }

      return {
        id: filePath,
        name: file.name,
        type: file.type,
        url: publicUrl,
        size: file.size,
        metadata: {
          contentType: file.type,
          originalName: file.name
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo';
      toast.error(message);
      throw error;
    }
  },

  async deleteFromSupabase(id: string, bucket: string = 'general'): Promise<void> {
    try {
      // For news images, try to delete from both database and storage
      if (bucket === 'news') {
        // Try to delete from database first
        const { error: deleteDbError } = await supabase
          .from('news_images')
          .delete()
          .eq('id', id);

        if (deleteDbError) {
          console.warn('Could not delete from news_images table:', deleteDbError);
        }

        // Then try to delete directly using the ID as the file path
        const { error: deleteError } = await supabase.storage
          .from(bucket)
          .remove([id]);

        if (deleteError) {
          console.warn('Could not delete file directly:', deleteError);
        } else {
          // If direct deletion worked, we're done
          return;
        }
      }

      // For other buckets or if news image not found in database, try to find the file in storage
      const { data: storageFiles, error: storageError } = await supabase.storage
        .from(bucket)
        .list();

      if (storageError) throw storageError;

      const fileToDelete = storageFiles?.find(file => file.id === id || file.name === id);
      
      if (fileToDelete) {
        const { error: deleteError } = await supabase.storage
          .from(bucket)
          .remove([fileToDelete.name]);

        if (deleteError) {
          console.error('Error deleting from storage:', deleteError);
          throw deleteError;
        }

        return;
      }

      throw new Error(`File not found in ${bucket} storage`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir arquivo';
      console.error('Delete error:', error);
      throw error;
    }
  },

  async listMediaFiles(folder: string): Promise<MediaItem[]> {
    try {
      console.log('Listing media files for folder:', folder);
      const bucket = getBucketFromFolder(folder);
      
      // First get files from storage
      const { data: storageFiles, error: storageError } = await supabase.storage
        .from(bucket)
        .list();

      if (storageError) throw storageError;
      console.log('Found files:', storageFiles?.length || 0);

      // Get public URLs and create media items
      const mediaItems = await Promise.all(
        (storageFiles || []).filter(file => !file.name.startsWith('.')).map(async (file) => {
          // Get public URL from Supabase storage
          const { data: { publicUrl } } = supabase.storage
            .from(bucket)
            .getPublicUrl(file.name);

          console.log('Generated public URL for file:', {
            name: file.name,
            bucket: bucket,
            publicUrl
          });

          return {
            id: file.id || file.name,
            name: file.name,
            type: file.metadata?.mimetype?.startsWith('image/') ? 'image' : 'audio',
            url: publicUrl,
            size: file.metadata?.size || 0,
            metadata: file.metadata,
            folder: folder,
            bucket: bucket,
            created_at: file.created_at || new Date().toISOString(),
            updated_at: file.created_at || new Date().toISOString()
          };
        })
      );

      console.log('Processed media items:', mediaItems);
      return mediaItems;

    } catch (error) {
      console.error('Error listing media files:', error);
      throw error;
    }
  },

  async renameFile(fileId: string, newName: string): Promise<void> {
    try {
      // Get file extension from the fileId/name
      const fileToRename = fileId.includes('.') ? fileId : `${fileId}.jpg`;
      const originalExt = fileToRename.split('.').pop() || '';

      // Remove any file extension from the new name
      const cleanNewName = newName.replace(/\.[^/.]+$/, '');
      
      // Create new filename with original extension
      const newFileName = `${cleanNewName}.${originalExt}`;

      // Get files from storage
      const { data: storageFiles, error: storageError } = await supabase.storage
        .from('general')
        .list();

      if (storageError) throw storageError;

      const fileToFind = storageFiles?.find(file => file.id === fileId || file.name === fileToRename);
      
      if (!fileToFind) {
        throw new Error('File not found in storage');
      }

      // Copy file with new name
      const { error: copyError } = await supabase.storage
        .from('general')
        .copy(fileToFind.name, newFileName);

      if (copyError) throw copyError;

      // Delete old file
      const { error: deleteError } = await supabase.storage
        .from('general')
        .remove([fileToFind.name]);

      if (deleteError) throw deleteError;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao renomear arquivo';
      throw error;
    }
  },
};

export { mediaService };